import type { ReactNode } from 'react';
import { Link, type LinkProps } from 'react-router-dom';
import { cx } from './cx.js';
import { SuitGlyph, SUIT_TINTS, type SuitName } from './SuitLine.js';

/** The four doors, one per suit. */
export type MenuTileSuit = SuitName;

/**
 * A door on the home menu: a suit, what is behind it, and why.
 *
 * There are four, one per suit, and that is the conceit — a fifth would break
 * it. They were four hand-written copies of the same twelve classes, and the
 * copies had already grown two variants nobody had named: one loud and three
 * quiet. Naming them is most of what this component does.
 *
 * A door is a chip at door size, and it is the biggest one the brand draws: the
 * same white face and 2px ink stroke as a button, with the deeper of the two
 * offset shadows, because a door is meant to look like something you could push
 * open. The primary door is lemon, and it is the only lemon on the menu — one
 * action color per screen is the whole argument for having one.
 *
 * The skin used to come from `buttonLook` in `Button.tsx`, so a tile and a
 * button could not drift apart. They now sit on different rungs of the shadow
 * ladder: a chip carries 3px and a door carries 4px, which is the brand's way
 * of saying which of the two is the bigger thing. Sharing a table across that
 * would mean one of them lying about its tier, so the tile keeps its own — two
 * short tables that disagree on one property, rather than one that is wrong for
 * whichever caller it was not written for.
 *
 * The suit sits straight on the face, the way the winning sketch drew its
 * doors. There was a medallion here for a day — the glyph in a circle of the
 * page ground, ringed in ink/25 — and it retired on 2026-08-27: a third
 * surface color inside every door read as a coin stuck to the chip, and the
 * circle muted the very accents the door exists to show.
 *
 * The hint no longer moves with it, and that is what the brand change bought.
 * Both faces are light now, so `text-gray-700` is legible on either, and the
 * second per-variant table went with it.
 *
 * The glyph is `size-7`, 28px, drawn straight on the face, with `gap-3`
 * to the name: `gap-4` read as a hole beside the suit on a phone (Mitch,
 * 2026-09-21). The hint is the base weight, not medium, from the same day.
 *
 * A door reacts like the button it is. That was the last thing the four copies
 * were missing, and for a day this component kept the gap on purpose: the four
 * biggest targets on the front door were the only controls in the product that
 * did nothing when a player pointed at one. The call was made on 2026-08-27 —
 * a door that does not move is a picture of a door.
 *
 * It is the `Button` reaction one rung up the shadow ladder, not a new idea:
 * `wash-ink` washes the white face and `lemon-deep` the lemon one, and the press
 * is the same settle — the door moves one pixel toward its own shadow and the
 * shadow shrinks by the same pixel, 4px to 3px, so it looks pushed onto the
 * page rather than thrown off it. Nothing lifts and nothing grows: the brand
 * settles, it does not bounce.
 */

export type MenuTileVariant =
  /** The one door being pushed. Lemon, and there is one per menu. */
  | 'primary'
  /** The rest of the menu. */
  | 'default';

export type MenuTileSize =
  /** A menu of doors, each a name and a hint. */
  | 'menu'
  /** The home poster's stack: a taller door with its name at display size. */
  | 'poster'
  /**
   * The poster's featured door, the one that outranks Play: the poster door
   * with its name at display size even over a second line, so it stands
   * taller than the doors under it. The Resume door (`poster-live`).
   */
  | 'feature';

/**
 * The tile's own shape. A button's geometry is too tight for two lines.
 *
 * No weight in here: the label and the hint each carry one below, and a second
 * font-weight utility on the same element is decided by Tailwind's emission
 * order rather than by anyone's intent. See the note on `buttonLook`.
 */
const SHAPE =
  'inline-flex w-full items-center justify-start rounded-3xl border-2 text-left no-underline transition-control duration-150 ease-out active:translate-x-px active:translate-y-px';

/**
 * What a size owns: the door's height, padding and gap, the suit's size, and
 * the type. The face, the stroke, the shadow and the reaction do not change
 * with it, so a poster door is still the tile.
 *
 * `poster` is the `lobby-home` sketch's door, chosen on 2026-09-07, and it
 * steps up at the `tablet` breakpoint with the rest of the poster. Its doors
 * share one height (Mitch asked for Play again at Play's height), so a door
 * with a hint sets its name a step down to fit both lines in that height. A
 * door with a name alone sets it at display size.
 */
const SIZE = {
  menu: {
    box: 'min-h-12 gap-3 px-5 py-4',
    glyph: 'size-7',
    label: 'font-extrabold',
    labelAlone: 'font-extrabold',
    hint: 'text-gray-700',
  },
  poster: {
    box: 'min-h-19 gap-4 px-6 py-4 tablet:min-h-24 tablet:gap-5 tablet:px-8',
    glyph: 'size-8 tablet:size-10',
    label: 'text-base leading-tight font-extrabold tablet:text-lg',
    labelAlone: 'text-2xl leading-none font-extrabold tracking-tight tablet:text-3xl',
    hint: 'text-sm text-gray-700',
  },
  feature: {
    box: 'min-h-19 gap-4 px-6 py-4 tablet:min-h-24 tablet:gap-5 tablet:px-8',
    glyph: 'size-8 tablet:size-10',
    label: 'text-2xl leading-none font-extrabold tracking-tight tablet:text-3xl',
    labelAlone: 'text-2xl leading-none font-extrabold tracking-tight tablet:text-3xl',
    hint: 'text-sm text-gray-700',
  },
} satisfies Record<
  MenuTileSize,
  { box: string; glyph: string; label: string; labelAlone: string; hint: string }
>;

/** The door's face. Color, stroke and shadow, standing still. */
const TONE = 'border-ink text-ink shadow-md shadow-ink';

/**
 * What a door does under the pointer: `Button`'s `REACTION`, one rung up.
 *
 * The hover wash is the same pair for the same reason — `wash-ink` is the one
 * wash that darkens a white face and a lemon one alike, and `lemon-deep` is
 * lemon's own pressed color rather than a wash over it. The press settles into
 * `shadow-sm shadow-ink`, which is 3px, because a door stands at 4px; a chip's 2px
 * would be a two-pixel drop and read as a fall.
 *
 * No `not-disabled:` guards, unlike `Button`: a tile is always a `<Link>`, and
 * an `<a>` is never `:disabled`. The guards exist there so one table can serve
 * a `<button>` and a link without being written twice. This table has one
 * element to serve.
 */
const REACTION = {
  default: 'hover:wash-ink active:shadow-sm',
  primary: 'hover:bg-lemon-deep active:shadow-sm',
} satisfies Record<MenuTileVariant, string>;

/**
 * What stands at the door's left edge: a suit, or something the door says in
 * place of one.
 *
 * `lead` is the Resume door's (2026-09-30): the saved score where the suit
 * would be, because a game in progress is named by its score, not by a suit.
 * The caller draws it and owns its accessible text.
 */
type MenuTileMark =
  | {
      /** The suit on the door. Decorative: the tile's name is the label beside it. */
      readonly suit: MenuTileSuit;
      readonly lead?: never;
    }
  | {
      readonly lead: ReactNode;
      readonly suit?: never;
    };

export type MenuTileProps = Omit<LinkProps, 'className' | 'style' | 'children'> &
  MenuTileMark &
  MenuTileContent;

interface MenuTileContent {
  readonly variant?: MenuTileVariant;
  /**
   * `menu` unless the door stands in the home poster's stack: `poster` there,
   * and `feature` for the one door that outranks Play.
   */
  readonly size?: MenuTileSize;
  /** What is behind the door. */
  readonly label: ReactNode;
  /**
   * One short line on why a player would open it. A poster door leaves it off
   * when its name says everything: Play needs no second line.
   */
  readonly hint?: ReactNode;
  /**
   * What the door costs, or what is behind it in one figure: a price, a
   * count. Pinned to the right edge, so a column of doors lines its figures
   * up whatever the names beside them run to. The plan doors on the offer
   * wear it (2026-09-21); a menu door has nothing to say there and leaves it
   * off.
   */
  readonly trailing?: ReactNode;
  /** Layout only. The tile's shape is not the caller's to change. */
  readonly className?: string | undefined;
}

export function MenuTile({
  variant = 'default',
  size = 'menu',
  suit,
  lead,
  label,
  hint,
  trailing,
  className,
  ...rest
}: MenuTileProps): React.JSX.Element {
  const current = rest['aria-current'] === 'page';
  const look = SIZE[size];
  return (
    <Link
      className={cx(
        SHAPE,
        look.box,
        TONE,
        current ? 'active:shadow-sm' : REACTION[variant],
        current ? 'bg-ground-dark' : variant === 'primary' ? 'bg-lemon' : 'bg-ground-light',
        className,
      )}
      {...rest}
    >
      {/*
        The glyph is `SuitGlyph`, the motif's own drawing, not the face's suit
        characters: Cabinet Grotesk's suits are lighter than the brand's, and
        the door is where the motif is read at its biggest.

        Its color on a white door is `SUIT_TINTS`, read off `SuitLine` rather
        than restated here — the door and the line draw the same motif, so a
        second copy of that table is two brands waiting to disagree. The
        winning sketch's door set had the club in teal, and that split retired
        on 2026-08-27 with the medallion: two teal doors on a four-door menu
        read as a repeat, and with no ink glyph on any white door nothing
        anchored the accents.

        On the lemon door the glyph is always ink — an accent on the action
        color would be two colors arguing over one door.
      */}
      {suit === undefined ? (
        lead
      ) : (
        <SuitGlyph
          suit={suit}
          className={cx(look.glyph, variant === 'primary' ? 'text-ink' : SUIT_TINTS[suit])}
        />
      )}
      <span className="flex min-w-0 flex-col gap-1">
        <span className={hint === undefined ? look.labelAlone : look.label}>{label}</span>
        {hint === undefined ? null : <span className={look.hint}>{hint}</span>}
      </span>
      {trailing === undefined ? null : (
        <span className="ml-auto shrink-0 self-center pl-2">{trailing}</span>
      )}
    </Link>
  );
}
